import {
	getAllAssignedMachines,
	unassignMachineFromCustomer,
} from '@/redux/actions/gpuActions';
import { Card, CardBody } from '@/shared/components/Card';
import ConfirmationModal from '@/shared/components/ConformationModal';
import ReactDataTableComponent from '@/shared/components/ReactDataTableComponent';
import { TableSkeleton } from '@/shared/components/Skeleton/TableSkeleton';
import ToolTipButton from '@/shared/components/ToolTipButton';
import dayjs from 'dayjs';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useGpuOneFilter from '../hooks/useGpuOneFilter';
import GpuOneFilterSection from './GpuOneFilterSection';
const ViewDetailsModal = lazy(() => import('./GpuOneViewDetailsModal'));

const GpuOneAssignedMachinesTable = () => {
	const dispatch = useDispatch();
	const { assignedGPUOne, isLoading } = useSelector(state => state.gpuOne);

	const {
		machines = [],
		total = 0,
		currentPage = 1,
		perPage = 10,
		sortBy = '-last_updated',
	} = assignedGPUOne || {};

	// Use the custom hook for filtering
	const {
		filters,
		apiFilters,
		handleFilterChange,
		handleSortChange,
		handleClearFilters,
		getApiFilters,
	} = useGpuOneFilter(machines);

	const [sortField, setSortField] = useState('last_updated');
	const [sortAsc, setSortAsc] = useState(false);
	const [rowData, setRowData] = useState(null);
	const [viewDetailsModal, setViewDetailsModal] = useState(false);
	const [showUnassignConfirmation, setShowUnassignConfirmation] =
		useState(false);
	const fetchData = async (page, numPerPage, sortByParam) => {
		const params = {
			page: page || currentPage,
			perPage: numPerPage || perPage,
			sortBy: sortByParam || apiFilters.sort || sortBy,
			filters: getApiFilters(),
		};

		dispatch(getAllAssignedMachines(params));
	};

	const handleSort = (column, direction) => {
		setSortField(column?.id);
		setSortAsc(direction === 'asc');
		const sortByParam = column?.id
			.split(',')
			.map(x => (direction !== 'asc' ? `-${x}` : x))
			.join(',');

		fetchData(1, undefined, sortByParam);
	};

	const handlePageChange = async page => {
		fetchData(page);
	};

	const handlePerRowsChange = async (newPerPage, page) => {
		fetchData(page, newPerPage);
	};

	const viewDetails = rowData => {
		console.log(rowData);
		setViewDetailsModal(true);
		setRowData(rowData);
	};

	const handleUnassignMachine = async row => {
		setRowData(row); // Set rowData for modal
		setShowUnassignConfirmation(true);
	};

	const confirmUnassign = async () => {
		try {
			await dispatch(unassignMachineFromCustomer(rowData.system_serial));
			// Refresh the data after unassignment
			fetchData(1, 10, '-last_updated');
			setShowUnassignConfirmation(false);
		} catch (error) {
			console.error('Error unassigning machine:', error);
			setShowUnassignConfirmation(false);
		}
	};

	const cancelUnassign = () => {
		setShowUnassignConfirmation(false);
	};

	useEffect(() => {
		fetchData(1, 10, '-last_updated');
	}, []);

	const prevApiFiltersRef = useRef();

	// Re-fetch data when API filters change (not on every keystroke)
	// Always reset to page 1 when filters change to show all filtered results
	useEffect(() => {
		const apiFiltersString = JSON.stringify(apiFilters);
		const prevApiFiltersString = JSON.stringify(prevApiFiltersRef.current);

		if (apiFiltersString !== prevApiFiltersString) {
			prevApiFiltersRef.current = apiFilters;
			fetchData(1, perPage, apiFilters.sort || sortBy);
		}
	}, [apiFilters, perPage, sortBy]);

	const columns = [
		{
			id: 'machine_name',
			name: 'Machine Name',
			selector: row => row.machine_name,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p
						style={{ cursor: 'pointer', textDecoration: 'underline' }}
						onClick={() => viewDetails(row)}
					>
						{row.machine_name}
					</p>
				</div>
			),
		},
		{
			id: 'maas_id',
			name: 'MAAS ID',
			selector: row => row.maas_id,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.maas_id}</p>
				</div>
			),
		},
		{
			id: 'system_vendor',
			name: 'System Vendor',
			selector: row => row.system_vendor,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.system_vendor}</p>
				</div>
			),
		},
		{
			id: 'system_product',
			name: 'System Product',
			selector: row => row.system_product,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.system_product}</p>
				</div>
			),
		},
		{
			id: 'system_serial',
			name: 'System Serial',
			selector: row => row.system_serial,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.system_serial}</p>
				</div>
			),
		},
		{
			id: 'total_cpu_cores',
			name: 'CPU Cores',
			selector: row => row.total_cpu_cores,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.total_cpu_cores}</p>
				</div>
			),
		},
		{
			id: 'cpu_type',
			name: 'CPU Type',
			selector: row => row.cpu_type,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.cpu_type}</p>
				</div>
			),
		},
		{
			id: 'total_memory',
			name: 'Memory (GB)',
			selector: row => row.total_memory,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.total_memory}</p>
				</div>
			),
		},
		{
			id: 'machine_status',
			name: 'Status',
			selector: row => row.machine_status,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.machine_status}</p>
				</div>
			),
		},
		{
			id: 'deployed_os',
			name: 'Deployed OS',
			selector: row => row.deployed_os,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>{row.deployed_os}</p>
				</div>
			),
		},
		{
			id: 'last_updated',
			name: 'Last Updated',
			selector: row => row.last_updated,
			sortable: true,
			reorder: true,
			cell: row => (
				<div>
					<p>
						{row.last_updated
							? dayjs(row.last_updated).format('MM/DD/YYYY hh:mm A')
							: 'N/A'}
					</p>
				</div>
			),
		},
		{
			id: 'actions',
			name: 'Actions',
			sortable: false,
			reorder: true,
			width: '100px',
			cell: row => (
				<ToolTipButton
					variant="danger"
					iconType="delete"
					size="iconButton"
					text="Unassign Machine"
					handleSubmit={() => handleUnassignMachine(row)}
				/>
			),
		},
	];

	return (
		<Card>
			<CardBody>
				{/* Filter Section */}
				<GpuOneFilterSection
					filters={filters}
					onFilterChange={handleFilterChange}
					onSortChange={handleSortChange}
					onClearFilters={handleClearFilters}
				/>

				<TableSkeleton
					data-testid="loading-wrap"
					isLoading={isLoading}
					rowsCount={8}
					columnsCount={4}
					showPagination={true}
					headerCellWidth="150px"
					rowCellWidth="130px"
				>
					{machines.length > 0 ? (
						<div className="assignedMachinesTable">
							<ReactDataTableComponent
								columns={columns}
								data={machines}
								pagination
								progressPending={isLoading}
								responsive={true}
								highlightOnHover
								paginationTotalRows={total}
								paginationServer
								onChangePage={handlePageChange}
								paginationPerPage={perPage}
								onChangeRowsPerPage={handlePerRowsChange}
								paginationRowsPerPageOptions={[10, 20, 30, 40, 100]}
								paginationDefaultPage={currentPage}
								persistTableHead
								sortServer
								onSort={handleSort}
								defaultSortFieldId={sortField}
								defaultSortAsc={sortAsc}
							/>
						</div>
					) : (
						<p style={{ textAlign: 'center', fontSize: '16px' }}>
							No data found
						</p>
					)}
				</TableSkeleton>
			</CardBody>

			{viewDetailsModal && (
				<Suspense fallback={<div>Loading...</div>}>
					<ViewDetailsModal
						modal={viewDetailsModal}
						setModal={setViewDetailsModal}
						toggle={() => setViewDetailsModal(false)}
						rowData={rowData}
						fetchData={fetchData}
					/>
				</Suspense>
			)}

			{showUnassignConfirmation && (
				<ConfirmationModal
					onCancel={cancelUnassign}
					onConfirm={confirmUnassign}
					title="Confirm Unassignment"
					message={`Are you sure you want to unassign the machine "${rowData?.machine_name}"? This action cannot be undone.`}
					confirmButtonText="Unassign Machine"
					variant="danger"
				/>
			)}
		</Card>
	);
};

export default GpuOneAssignedMachinesTable;
